/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import * as React from 'react';

/**
 * Creates a simple hook to apply partial updates
 * to state objects. It eliminates the need of having
 * to spread existing object properties with new partial
 * updates to get the new state snapshot of the object.
 *
 * @example
 *
 * const [value, setValue] = useUpdateState({ a: 10, b: 'hello', c: true });
 * setValue({ b: 'hi' }); // value is now { a: 10, b: 'hi', c: true }
 * setValue({ d: 'example' }); // Compiler error. 'd' is not part of the object.
 *
 * @param initialValue An object or a factory function that produces an object
 * that is not a primitive.
 */
export const useUpdateState = <T extends object>(initialValue: T | (() => T)): [T, React.Dispatch<Partial<T>>] => {
    const [stateObject, setStateObject] = React.useState<T>(initialValue);

    /**
     * A simple function to spread the given partial
     * object with the original state object to create
     * a new state instance.
     *
     * @param partialObject The partial object to update
     * the state with.
     */
    const updateStateObject = React.useCallback(
        (partialObject: Partial<T>): void => setStateObject(prevStateObject => ({ ...prevStateObject, ...partialObject })),
        [setStateObject]
    );

    return [stateObject, updateStateObject];
};
